/**
 * author: arczhang
 * description: 改版菜单，将菜单拆分成 3 个部分放到 3 个不同地方，每个地方的菜单只有一级
 */
import React, { useMemo, useCallback, useEffect } from 'react';
import { matchRoutes } from 'react-router-config';
import { NavLink, useLocation } from 'react-router-dom';
import { Menu } from '@tencent/spaui';
import get from 'lodash/get';

import { authRoute } from '../../utils/routes';
import { canIUseLogs } from '../pages/logs';

const PathToRoute = {};

/**
 * 从路由树取出是菜单的项
 * 完整的路由包含所有的页面，但并不是所有页面的入口都是菜单
 * 比如：菜单xxx列表对应一个页面
 * 但是创建xxx是这个列表页的子页面，但并没有对应的菜单入口
 * 这类路由项需要过滤掉
 */
const getMenuTree = arr => {
  const getTreeNode = routes => {
    const menuArr = [];
    // 没有子菜单直接跳过
    if (!Array.isArray(routes)) return menuArr;
    routes.forEach(item => {
      const routeItem = { ...item };
      // 不是菜单的路由直接跳过不管,注意第一个层级'/'虽然不是菜单但是需要保留
      if (!routeItem.isMenu && routeItem.path !== '/') return;
      // 菜单数据结构用不到component，减小数据体积
      delete routeItem.component;
      // 有子菜单的情况下进行递归
      if (Array.isArray(routeItem.routes)) {
        routeItem.routes = getTreeNode(routeItem.routes);
      }
      // 是菜单的项进行路径到路由对象的映射
      PathToRoute[routeItem.path] = routeItem;
      menuArr.push(routeItem);
    });
    return menuArr;
  };

  return getTreeNode(arr);
};

// 如果需要支持异步，这里不能为了减少计算次数而做缓存
const getAuthedMenu = () => {
  const authedRoutes = authRoute();
  return getMenuTree(authedRoutes);
};

/**
 * 获取当前层级的菜单路由
 */
const getMenuLv = n => pathname => {
  const MenuTree = getAuthedMenu();

  const fn = _n => _pathname => {
    const matched = matchRoutes(MenuTree, _pathname);
    const currentLv = get(matched, [_n, 'route', 'path']);
    if (currentLv) return currentLv;
    return get(PathToRoute[fn(_n - 1)(_pathname)], 'routes[0].path');
  };

  return fn(n)(pathname);
};

/**
 * 第一级菜单，位于导航栏所在的地方
 * 最简单的逻辑，不用判断上一级菜单
 */
export const MenuLv1 = () => {
  const { pathname } = useLocation();

  /**
   * 重要！
   * activeNav 一定要写在 lv1Menus 之前
   * 保证 getMenuLv 在 使用 PathToRoute 之前调用
   * 否则 PathToRoute 会是一个不满足权限过滤后的数据
   */
  const activeNav = useMemo(() => {
    // 必须在此处先调用 getMenuLv，否则 PathToRoute 将会是个空对象
    const active =  getMenuLv(1)(pathname);
    // 消息中心是个独立页面，做特殊处理
    if (/^\/message/.test(pathname)) return '';
    return active;
  }, [pathname]);

  const lv1Menus = get(PathToRoute, ['/', 'routes'], []);

  const getActive = useCallback(nav => () => nav === activeNav);

  // 进入系统后并处于二级密码验证的时候不显示一级菜单
  if (pathname === '/secondLogin') return null;

  return (
    <div className="dispersive-menu-lv-1">
      <div className="dml1-item">
        {
          lv1Menus.map(menu => (
            <NavLink
              to={menu.path}
              key={menu.path}
              className="dml1-link"
              activeClassName="lv-1-active"
              isActive={getActive(menu.path)}
            >{ menu.title }</NavLink>
          ))
        }
      </div>
    </div>
  );
};

const IconComponent = ({ iconClassName }) => {
  return <i className={`menu-icon ${iconClassName}`} />;
};

/**
 * 第二级菜单，需要考虑路由只有一级的情况
 */
export const MenuLv2 = () => {
  const { pathname } = useLocation();

  const currentMenu = useMemo(
    () => getMenuLv(2)(pathname),
    [pathname]
  );

  const lv2Menu = (() => {
    const menuArr = get(
      PathToRoute,
      [getMenuLv(1)(pathname), 'routes'],
      []
    );
    return menuArr.map(item => ({
      label: item.title,
      href: `#${item.path}`,
      iconClassName: item.iconClassName,
    }));
  })();

  const handleTypeChange = (_, type) => {
    const map = {
      normal: '172px',
      mini: '46px',
    };
    const html = document.documentElement;
    const css = window.getComputedStyle(html);
    html.style.setProperty('--side-menu-width', map[type]);
  };

  // 某些页面没有菜单组件，卸载的时候需要重置切换菜单类型的副作用
  useEffect(() => {
    const menuElm = document.querySelector('#spaui-menu_1');
    // 有菜单DOM的情况下无需重置状态的操作
    if (menuElm) return;
    handleTypeChange(null, 'normal');
  }, [handleTypeChange]);

  const handleGoToLogs = () => {
    window.location.href = '#/logs';
  };

  // 仅超级管理员、管理员、免登录有权限查看操作日志
  const hasLogsRight = canIUseLogs();

  return (
    <aside className="sidebar">
      <Menu
        // toggle
        // type="normal"
        value={`#${currentMenu}`}
        data={lv2Menu}
        className="new-menu"
        onTypeChange={handleTypeChange}
        IconComponent={IconComponent}
        triggerWindowResizeWhenTypeChange
        // onChange={this.onChange}
        // ItemComponent={MenuItemComponent}
      />
      {
        hasLogsRight && (
          <div className="bottom-menu">
            <div className="bottom-menu__item" onClick={handleGoToLogs}>
              <div className="bottom-menu__icon svg-icon-menu-logs"></div>
              <div className="bottom-menu__content">操作日志</div>
            </div>
          </div>
        )
      }
    </aside>
  );
};

/**
 * 第三级菜单
 */
export const MenuLv3 = () => {
  const { pathname } = useLocation();

  // 需要异步更新的一级菜单不能使用 useMemo 进行缓存
  const lv3Menu = (() => {
    const menuArr = get(
      PathToRoute,
      [getMenuLv(2)(pathname), 'routes'],
      []
    );
    return menuArr.map(item => ({
      label: item.title,
      href: item.path,
    }));
  })();

  const activeNav = useCallback(href => {
    const currentPath = getMenuLv(3)(pathname);
    return href === currentPath;
  }, [pathname]);

  return (
    <div className="sec sec-top-tab">
      <div className="datalist-tabs spaui-tabs">
        <div className="spaui-tabs-head">
          <ul className="spaui-tabs-nav">
            {
              lv3Menu.map(menu => (
                <li key={menu.href} className={activeNav(menu.href) ? 'active' : ''}>
                  <NavLink
                    to={menu.href}
                    key={menu.href}
                    // isActive貌似不能用boolean
                    // isActive={false}
                  >{ menu.label }</NavLink>
                </li>
              ))
            }
          </ul>
        </div>
      </div>
    </div>
  );
};

export default null;
